import { Link } from "react-router-dom";
import TeamBadge from "@/components/base/TeamBadge";
import StatusBadge from "@/components/base/StatusBadge";
import { matches } from "@/mocks/matches";
import { formatDateCN, formatWeekday, formatTime, relativeDayLabel } from "@/utils/format";

const DAYS = ["2026-10-12", "2026-10-13", "2026-10-14"];

export default function ScheduleTimeline() {
  const byDay = DAYS.map((day) => ({
    day,
    items: matches
      .filter((m) => m.date === day)
      .sort((a, b) => a.startTime.localeCompare(b.startTime)),
  }));

  return (
    <section className="w-full px-4 md:px-6 py-12 md:py-16">
      <div className="flex items-end justify-between gap-4 mb-6">
        <div>
          <span className="font-label text-xs tracking-[0.3em] text-accent-600 uppercase">
            Schedule
          </span>
          <h2 className="font-heading font-semibold text-2xl md:text-3xl text-foreground-950 mt-1">
            赛程预告
          </h2>
        </div>
        <Link
          to="/schedule"
          className="hidden sm:inline-flex items-center gap-1 text-sm text-primary-700 hover:text-primary-800 cursor-pointer whitespace-nowrap"
        >
          完整赛程
          <i className="ri-arrow-right-s-line" />
        </Link>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5">
        {byDay.map(({ day, items }, idx) => (
          <div
            key={day}
            className={`rounded-lg border p-4 md:p-5 ${
              idx === 0
                ? "bg-primary-950 border-primary-800"
                : "bg-background-100 border-background-200"
            }`}
          >
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <span
                  className={`inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-label font-semibold ${
                    idx === 0
                      ? "bg-accent-500 text-background-50 dark:text-foreground-950"
                      : "bg-primary-100 text-primary-700"
                  }`}
                >
                  {relativeDayLabel(day) || "赛程日"}
                </span>
                <span
                  className={`font-heading text-sm ${
                    idx === 0 ? "text-background-50" : "text-foreground-900"
                  }`}
                >
                  {formatDateCN(day)} {formatWeekday(day)}
                </span>
              </div>
            </div>

            {items.length === 0 ? (
              <p className={`text-sm py-6 text-center ${idx === 0 ? "text-primary-300" : "text-foreground-500"}`}>
                当日暂无安排比赛
              </p>
            ) : (
              <div className="space-y-3">
                {items.slice(0, 4).map((m) => (
                  <Link
                    key={m.id}
                    to={`/match/${m.id}`}
                    className={`block p-3 rounded-md transition-colors cursor-pointer ${
                      idx === 0 ? "hover:bg-primary-900" : "hover:bg-background-200"
                    }`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <span
                        className={`font-label text-sm font-semibold ${
                          idx === 0 ? "text-accent-400" : "text-accent-600"
                        }`}
                      >
                        {formatTime(m.startTime)}
                      </span>
                      <StatusBadge status={m.status} />
                    </div>
                    <div className="flex items-center gap-2">
                      <TeamBadge shortName={m.home.shortName} color={m.home.color} size="sm" />
                      <span
                        className={`text-xs ${idx === 0 ? "text-primary-200" : "text-foreground-600"}`}
                      >
                        {m.home.shortName}
                      </span>
                      <span className={idx === 0 ? "text-primary-400" : "text-foreground-400"}>
                        vs
                      </span>
                      <span
                        className={`text-xs ${idx === 0 ? "text-primary-200" : "text-foreground-600"}`}
                      >
                        {m.away.shortName}
                      </span>
                      <TeamBadge shortName={m.away.shortName} color={m.away.color} size="sm" />
                    </div>
                    <p
                      className={`mt-2 text-[11px] ${
                        idx === 0 ? "text-primary-400" : "text-foreground-500"
                      }`}
                    >
                      {m.leagueName} · {m.stage}
                    </p>
                  </Link>
                ))}
              </div>
            )}

            <Link
              to="/schedule"
              className={`mt-3 inline-flex items-center gap-1 text-xs cursor-pointer whitespace-nowrap ${
                idx === 0 ? "text-accent-400 hover:text-accent-300" : "text-primary-700 hover:text-primary-800"
              }`}
            >
              查看该日全部比赛
              <i className="ri-arrow-right-s-line" />
            </Link>
          </div>
        ))}
      </div>
    </section>
  );
}